/**
 * 416 (the ambush as a lead skill; BUILDLIST 66.27, drill 5c): the walk out to an ambush hold's site, by the way the
 * enemy's eye does not see. An ambush laid in full view of the enemy it waits for is no ambush: at 25 a side the squads
 * walked straight out across open ground, were seen, and sprang it on the way (drill 5c on master: seen 4 of 4, sprung
 * 7 s in, 18 s before the enemy came).
 *
 * A tier option in the leader's step (squadLead.ts LeadArgs.skills; actionBot passes it for an ambush hold with the
 * commander's ambush on): asked after `hold`, so only outside the hold zone — inside, the hold step takes the posts. It
 * holds while there is a way to the zone that a standing body walks hidden from the eyes for less of its length than the
 * straight one, and no enemy is in contact; it gives a bound along that way, the leader's post a leg on, the members' in
 * file behind it, every one moving. Without it, or out of it, the step is as it was.
 *
 * The eyes are the way's: the enemies the leader knows of, the most confident first; with none, the way's eye — on the
 * line from the site toward the enemy's side, as far past the site as the site's own eyes (AMBUSH_PLAN.eyeBackPeople).
 * Pure.
 */
import type { GameState, LeadStep, PlayerState, Vec3 } from '../../types';
import { PERSON_BODY_HEIGHT } from '../../constants';
import { vecAdd, vecCross, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../../math';
import { dcos, dsin } from '../../core/dmath';
import { personLineOfSight, personPointOn } from '../../core/fineTerrain';
import { stanceSphereRadius } from '../../core/coverMap';
import { segmentCovered } from '../../core/bullets';
import type { WalkObstacle } from '../../core/surfaceWalk';
import type { PersonSet } from '../../core/scaleProfile';
import type { TierOption } from '../choice';
import { AMBUSH_PLAN } from './ambush';
import { arc, slerp } from './spots';

export const AMBUSH_WALK = {
  /** The lattice the way is found on: points this far apart (people), out to `marginPeople` beside the straight way and past its ends. */
  stridePeople: 2,
  marginPeople: 10,
  /**
   * The walk it takes: the last this many people to the site (farther out, the leader comes on as before — at 25 a side a
   * leader back from its base searched a 500-people lattice: 5,265 points, 104 ms a search).
   */
  walkPeople: 60,
  /** A search asks at most this many sight lines (an eye to a point); past them it gives the walk up this time (in play, ~0.03 ms a line). */
  sightLines: 120,
  /** A step of the way joins points within this many strides (a diagonal's 1.42 and a little). */
  linkStrides: 1.5,
  /** A seen stretch of the way costs this many times its length. */
  seenCost: 12,
  /** The skill takes the walk only when its way is seen for this many people less than the straight one. */
  gainPeople: 2,
  /** The eyes: the leader's known enemies, at most this many. */
  eyes: 3,
  /** A bound: the leader's post this far on along the way (people), each member's this much further back. */
  legPeople: 6,
  filePeople: 2,
  /** Contact: a known enemy this near the leader or a member (people) — the drill's, not the walk's (squadLead LEAD.contactPeople). */
  contactPeople: 22,
  /** The way found is followed while every eye is within this of the one it hid from (people) and the leader within `onWayPeople` of it — searched again after `reuseSeconds` or when either fails. */
  eyeMovePeople: 5,
  onWayPeople: 3,
  reuseSeconds: 10,
};

export interface AmbushWalkArgs {
  state: GameState;
  /** The leader's step's order key. */
  order: string;
  planetIndex: number;
  /** The site (the ambush hold's place: a surface direction) and its hold zone (world units: squadLead holdZone). */
  site: Vec3;
  zone: number;
  leader: PlayerState;
  /** The squad's members under the step, the leader first. */
  members: readonly PlayerState[];
  /** The enemies the leader knows of (world points, the most confident first). */
  known: readonly Vec3[];
  /** The enemy's side of the site (a surface direction: actionBot enemySide), for the way's eye with nothing known; null: none. */
  enemySide: Vec3 | null;
  ps: PersonSet;
  /** The tick's length (s). */
  dt: number;
  /** The walk's obstacles on the planet (the way goes round them). */
  obstacles: readonly WalkObstacle[];
  prev: LeadStep | null;
  tick: number;
}

interface Way { points: Vec3[]; seen: number; straightSeen: number }

/** The eyes the walk hides from (world points at a standing eye's height). */
export function walkEyes(a: Pick<AmbushWalkArgs, 'state' | 'planetIndex' | 'site' | 'known' | 'enemySide' | 'ps'>): Vec3[] {
  const planet = a.state.planets[a.planetIndex];
  if (!planet) return [];
  const C = planet.position, person = PERSON_BODY_HEIGHT * a.ps.k;
  const eyeAt = (d: Vec3) => personPointOn(planet, vecNormalize(d), a.ps, a.ps.losEyeHeight);
  if (a.known.length) return a.known.slice(0, AMBUSH_WALK.eyes).map(k => eyeAt(vecSub(k, C)));
  if (!a.enemySide) return [];
  const w = arc(a.site, a.enemySide);
  if (w < 1e-9) return [];
  return [eyeAt(slerp(a.site, a.enemySide, (AMBUSH_PLAN.eyeBackPeople[0] * person) / planet.radius / w))];
}

/**
 * The way from the leader to the site's hold zone: the cheapest path over a lattice round the straight way (round the
 * walk's obstacles), a seen stretch costing `seenCost` times its length, ending at a hidden point inside the zone; with
 * how much of it is seen (people), and how much of the straight way. A point is seen as the bots see (actionBot sightOf):
 * a standing eye there and an eye, the ground and the props between them. Null with no hidden point in the zone.
 */
export function hiddenWay(a: AmbushWalkArgs, eyes: readonly Vec3[]): Way | null {
  const planet = a.state.planets[a.planetIndex];
  if (!planet || eyes.length === 0) return null;
  const W = AMBUSH_WALK, R = planet.radius, person = PERSON_BODY_HEIGHT * a.ps.k;
  const from = vecNormalize(a.leader.surfaceDir), site = vecNormalize(a.site);
  // the lattice: a frame on the straight way (u along it from the leader, v across), in people
  const len = (arc(from, site) * R) / person;
  if (len < 1e-6 || len > W.walkPeople) return null;
  const across = vecNormalize(vecCross(from, site));
  const step = W.stridePeople, nu = Math.ceil((len + 2 * W.marginPeople) / step), nv = Math.ceil((2 * W.marginPeople) / step), cols = nv + 1;
  // (the lattice by rows: a row's point on the way once, then turned across it by each column's angle, kept per column —
  // across is square to every point of the way)
  const turns = Array.from({ length: cols }, (_, j) => { const th = ((-W.marginPeople + j * step) * person) / R; return [dcos(th), dsin(th)]; });
  const pts: Vec3[] = [];
  for (let i = 0; i <= nu; i++) {
    const on = slerp(from, site, (-W.marginPeople + i * step) / len);
    for (let j = 0; j < cols; j++) pts.push(vecNormalize(vecAdd(vecScale(on, turns[j][0]), vecScale(across, turns[j][1]))));
  }
  const n = pts.length, zone = a.zone;
  // the obstacles near: blocked within its radius and a standing body's (a dot product against each one's cosine)
  // (the corridor's: no farther across the way than the lattice and the obstacle, nor farther along it)
  const clear = stanceSphereRadius('stand', a.ps), farCos = dcos(((len + 2 * W.marginPeople) * person) / R);
  const near = a.obstacles.filter(o => {
    const d = vecNormalize(o.dir), side = (W.marginPeople * person + o.radius + clear) / R;
    return Math.abs(vecDot(d, across)) <= dsin(side) && vecDot(d, from) >= farCos && vecDot(d, site) >= farCos;
  }).map(o => ({ d: vecNormalize(o.dir), c: dcos((o.radius + clear) / R) }));
  const wall = new Uint8Array(n);
  for (let k = 0; k < n; k++) for (const o of near) if (vecDot(o.d, pts[k]) > o.c) { wall[k] = 1; break; }
  // hidden, asked as the search reaches a point (−1 not yet)
  const hidAt = new Int8Array(n).fill(-1);
  let lines = 0;
  const hid = (k: number): boolean => {
    if (hidAt[k] < 0) {
      const e = personPointOn(planet, pts[k], a.ps, a.ps.losEyeHeight);
      hidAt[k] = !wall[k] && eyes.every(eye => { lines++; return !personLineOfSight(eye, e, a.state) || segmentCovered(eye, e, a.state); }) ? 1 : 0;
    }
    return hidAt[k] === 1;
  };
  const inZone = (k: number) => arc(pts[k], site) * R <= zone;
  // the start: the lattice point nearest the leader
  let start = -1, sd = -Infinity;
  for (let k = 0; k < n; k++) { const dd = vecDot(pts[k], from); if (!wall[k] && dd > sd) { sd = dd; start = k; } }
  if (start < 0) return null;
  // A* over the lattice (its neighbours by index: a grid) to a hidden point in the zone, the length to the zone left the
  // estimate (a step costs its length at least); ties broken by index
  const reach = Math.floor(W.linkStrides);
  const toZone = (k: number) => Math.max(0, arc(pts[k], site) * R - zone) / person;
  const cost = new Float64Array(n).fill(Infinity), est = new Float64Array(n), back = new Int32Array(n).fill(-1), done = new Uint8Array(n);
  cost[start] = 0; est[start] = toZone(start);
  const heap: number[] = [start];
  const less = (x: number, y: number) => est[x] < est[y] || (est[x] === est[y] && x < y);
  const push = (k: number) => { heap.push(k); let c = heap.length - 1; while (c > 0) { const p = (c - 1) >> 1; if (!less(heap[c], heap[p])) break; [heap[c], heap[p]] = [heap[p], heap[c]]; c = p; } };
  const pop = (): number => { const top = heap[0], last = heap.pop()!; if (heap.length) { heap[0] = last; let c = 0; for (;;) { const l = 2 * c + 1, rr = l + 1; let m = c; if (l < heap.length && less(heap[l], heap[m])) m = l; if (rr < heap.length && less(heap[rr], heap[m])) m = rr; if (m === c) break; [heap[c], heap[m]] = [heap[m], heap[c]]; c = m; } } return top; };
  let goal = -1;
  while (heap.length) {
    const k = pop();
    if (lines > W.sightLines) return null;   // (the search's budget spent: the walk given up this time)
    if (done[k]) continue;
    done[k] = 1;
    if (inZone(k) && hid(k)) { goal = k; break; }
    const i = Math.floor(k / cols), j = k % cols, hk = hid(k);
    for (let di = -reach; di <= reach; di++) for (let dj = -reach; dj <= reach; dj++) {
      if (!di && !dj) continue;
      const ii = i + di, jj = j + dj;
      if (ii < 0 || ii > nu || jj < 0 || jj >= cols) continue;
      const q = ii * cols + jj;
      if (done[q] || wall[q]) continue;
      const hq = hid(q), l = step * Math.sqrt(di * di + dj * dj), c = cost[k] + l * (hk && hq ? 1 : hk || hq ? (1 + W.seenCost) / 2 : W.seenCost);
      if (c < cost[q]) { cost[q] = c; est[q] = c + toZone(q); back[q] = k; push(q); }
    }
  }
  if (goal < 0) return null;
  const chain: number[] = [];
  for (let k = goal; k >= 0; k = back[k]) chain.push(k);
  chain.reverse();
  const seenOf = (ks: readonly number[]) => { let s = 0; for (let x = 1; x < ks.length; x++) if (!hid(ks[x]) || !hid(ks[x - 1])) s += (arc(pts[ks[x]], pts[ks[x - 1]]) * R) / person; return s; };
  // the straight way: the lattice's middle row (v = 0, through the leader and the site) from the leader to the zone
  const row: number[] = [], j0 = Math.round(W.marginPeople / step);
  for (let i = Math.ceil(W.marginPeople / step); i <= nu; i++) { const k = i * cols + j0; row.push(k); if (inZone(k)) break; }
  return { points: chain.map(k => pts[k]), seen: seenOf(chain), straightSeen: seenOf(row) };
}

/** The point `people` along a way of points from its first (its last when the way is shorter). */
function along(points: readonly Vec3[], people: number, perPerson: number): Vec3 {
  let left = people * perPerson;
  for (let k = 1; k < points.length; k++) {
    const l = arc(points[k - 1], points[k]);
    if (l >= left) return slerp(points[k - 1], points[k], l > 0 ? left / l : 0);
    left -= l;
  }
  return points[points.length - 1];
}

/** The walk out as the leader's tier option ('ambushWalk'): see the module note. */
export function ambushWalk(a: AmbushWalkArgs): TierOption<string, LeadStep> {
  let way: Way | null | undefined, eyesNow: Vec3[] | undefined, found = a.tick;
  const planet = a.state.planets[a.planetIndex];
  const person = PERSON_BODY_HEIGHT * a.ps.k, R = planet?.radius ?? 1;
  const eyes = () => (eyesNow ??= walkEyes(a));
  // the way: the one the last step found, from the point on it nearest the leader, while its eyes and the leader hold to it
  const kept = (): Way | null => {
    const w = a.prev?.skill?.name === 'ambush' ? a.prev.skill.walk : undefined, e = eyes();
    if (!w || !planet || (a.tick - w.found) * a.dt >= AMBUSH_WALK.reuseSeconds || w.eyes.length !== e.length) return null;
    if (e.some((x, k) => vecLength(vecSub(x, w.eyes[k])) > AMBUSH_WALK.eyeMovePeople * person)) return null;
    let at = -1, best = Infinity;
    w.way.forEach((p, k) => { const dd = arc(p, a.leader.surfaceDir) * R; if (dd < best) { best = dd; at = k; } });
    if (at < 0 || best > AMBUSH_WALK.onWayPeople * person) return null;
    found = w.found;
    return { points: w.way.slice(at), seen: 0, straightSeen: AMBUSH_WALK.gainPeople };   // (its gain was read when it was found)
  };
  const wayOf = (): Way | null => (way !== undefined ? way : (way = kept() ?? hiddenWay(a, eyes())));
  return {
    kind: 'ambushWalk',
    when: () => {
      if (!planet || arc(a.leader.surfaceDir, a.site) * R <= a.zone) return false;   // (inside the hold zone: the hold step's)
      const close = AMBUSH_WALK.contactPeople * person;
      if (a.known.some(k => { const kd = vecNormalize(vecSub(k, planet.position)); return a.members.some(m => arc(kd, m.surfaceDir) * R < close); })) return false;
      const w = wayOf();
      return !!w && w.straightSeen - w.seen >= AMBUSH_WALK.gainPeople;
    },
    give: () => {
      const w = wayOf()!, perPerson = person / R;
      const lead = along(w.points, AMBUSH_WALK.legPeople, perPerson);
      const posts: Array<[number, Vec3]> = a.members.map((m, k) => [m.id, k === 0 ? lead : along(w.points, Math.max(0, AMBUSH_WALK.legPeople - k * AMBUSH_WALK.filePeople), perPerson)]);
      const eye = eyes()[0];
      const face = eye && planet ? vecNormalize(vecSub(eye, planet.position)) : vecNormalize(a.site);
      const was = a.prev?.skill?.name === 'ambush' && a.prev.skill.phase === 'walking out' ? a.prev : null;
      return {
        // (the way in is the site, not yet staged: a step after this one, the walk given up, works its way in afresh —
        // squadLead's way() keeps a staged step's `to`)
        order: a.order, kind: 'bound', planetIndex: a.planetIndex, to: vecNormalize(a.site), face, posts, moving: a.members.map(m => m.id),
        skill: { name: 'ambush', phase: 'walking out', since: was?.skill?.since ?? a.tick, walk: { way: w.points, eyes: eyes(), found } },
        since: was?.since ?? a.tick, at: a.tick, staged: false,
      };
    },
  };
}
